import { useEffect, useState } from 'react';
import {
  fetchAndActivate,
  getBoolean,
  getString,
} from 'firebase/remote-config';
import { remoteConfig } from '@/lib/firebase';

type FeatureFlagType = 'boolean' | 'string' | 'json';

type FeatureFlagReturn<T extends FeatureFlagType> = T extends 'boolean'
  ? boolean
  : T extends 'json'
    ? Record<string, unknown> | null
    : string;

const getDefaultValue = (type: FeatureFlagType) => {
  if (type === 'boolean') return false;
  if (type === 'json') return null;
  return '';
};

const useFeatureFlag = <T extends FeatureFlagType>(
  flagKey: string,
  type: T = 'boolean' as T
): FeatureFlagReturn<T> => {
  const [flagValue, setFlagValue] = useState<FeatureFlagReturn<T>>(
    getDefaultValue(type) as FeatureFlagReturn<T>
  );

  useEffect(() => {
    if (!remoteConfig) {
      console.warn('Remote Config is not available during SSR.');
      return;
    }

    const fetchFeatureFlag = async () => {
      try {
        await fetchAndActivate(remoteConfig);

        let value: FeatureFlagReturn<T> = getDefaultValue(
          type
        ) as FeatureFlagReturn<T>;

        if (type === 'boolean') {
          const boolValue = getBoolean(remoteConfig, flagKey);
          if (!boolValue) {
            const rawValue = getString(remoteConfig, flagKey)
              .toLowerCase()
              .trim();
            value = (
              ['true', '1', 'yes', 'on'].includes(rawValue) ? true : boolValue
            ) as FeatureFlagReturn<T>;
          } else {
            value = boolValue as FeatureFlagReturn<T>;
          }
        } else if (type === 'json') {
          const jsonString = getString(remoteConfig, flagKey);
          value = (
            jsonString ? JSON.parse(jsonString) : null
          ) as FeatureFlagReturn<T>;
        } else {
          value = getString(remoteConfig, flagKey) as FeatureFlagReturn<T>;
        }

        setFlagValue(value);
      } catch (error) {
        console.error('Error fetching or parsing feature flag:', error);
        setFlagValue(getDefaultValue(type) as FeatureFlagReturn<T>);
      }
    };

    fetchFeatureFlag();
  }, [flagKey, type]);

  return flagValue;
};

export default useFeatureFlag;
